import { useCallback, useEffect, useRef, useState } from 'react';

import type { CraftEmailCompetitor, CreateCraftEmailThreadRequest } from 'features/craft-email-agent';
import { appStore } from 'services/appStore';
import type { EmailPreview } from 'services/jsonParser/types';

import { useAppState } from '../../../AppStateProvider';
import type { Contact } from '../../../domain/contact';
import { Page } from '../../../domain/page';
import { useContact } from '../../../features/contacts/useContact';
import { useCreateCraftEmailThread } from '../../../features/craft-email-agent/useCreateCraftEmailThread';

import type { MessageKey, Recipient } from './types';
import { useAiOutreachTracking } from './useAiOutreachTracking';

const FAKE_LOADING_DELAY = 2000;

const contactToRecipient = (contact: Partial<Contact>): Recipient | null => {
  if (!contact.name?.trim()) {
    return null;
  }
  return {
    name: contact.name,
    role: contact.title || contact.role || '',
  };
};

const getContactKey = (contact: Partial<Contact> | null | undefined, contactLinkedinId?: string): string | null => {
  if (!contact?.name?.trim()) {
    return null;
  }
  const titleOrRole = contact.title || contact.role || '';
  return contact.id ?? contactLinkedinId ?? `${contact.name}\0${titleOrRole}`;
};

export const useAiOutreach = () => {
  const {
    aiOutreach: { domain: aiOutreachDomain, companyName: aiOutreachCompanyName },
    page,
    contactLinkedinId,
  } = useAppState();

  const { contact, isLoading: isLoadingContact } = useContact();
  const tracking = useAiOutreachTracking();

  const [payload, setPayload] = useState<CreateCraftEmailThreadRequest | null>(null);

  const {
    isLoading,
    isInitialLoading,
    isFollowUpLoading,
    isError,
    refetchCreate,
    reset,
    stop,
    email,
    emailPreviews,
    threadId,
    messageId,
    addUserMessage,
    sendMessage,
    applyPreview,
    competitors,
    applyCompetitors,
    timeline,
    scrollToTopAfterUserSend,
  } = useCreateCraftEmailThread(payload);

  const [selectedRecipient, setSelectedRecipient] = useState<Recipient | null>(null);
  const [showCompetitorPicker, setShowCompetitorPicker] = useState(false);
  const [showAlternatives, setShowAlternatives] = useState(false);
  const [isFakeLoading, setIsFakeLoading] = useState(false);
  const prevDomainRef = useRef(aiOutreachDomain);
  const lastFilledContactKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (prevDomainRef.current !== aiOutreachDomain) {
      prevDomainRef.current = aiOutreachDomain;
      stop();
      reset();
      setSelectedRecipient(null);
      setShowCompetitorPicker(false);
      setShowAlternatives(false);
      lastFilledContactKeyRef.current = null;
    }
  }, [aiOutreachDomain, stop, reset]);

  useEffect(() => {
    if (page !== Page.Contact) {
      lastFilledContactKeyRef.current = null;
      return;
    }

    if (isLoadingContact) {
      return;
    }

    const recipient = contact ? contactToRecipient(contact) : null;
    if (!recipient) {
      return;
    }

    const contactKey = getContactKey(contact, contactLinkedinId);
    if (!contactKey) {
      return;
    }

    if (lastFilledContactKeyRef.current === contactKey) {
      return;
    }

    lastFilledContactKeyRef.current = contactKey;
    setSelectedRecipient(recipient);
    reset();
  }, [page, isLoadingContact, contact, contactLinkedinId, reset]);

  useEffect(() => {
    if (emailPreviews.length > 0) {
      setShowAlternatives(true);
    }
  }, [emailPreviews]);

  const handleGenerate = useCallback(() => {
    if (!aiOutreachDomain) {
      return;
    }
    tracking.trackGenerate();
    setPayload({
      domain: aiOutreachDomain,
      emailContext: {
        attendees: selectedRecipient ? `${selectedRecipient.name}, ${selectedRecipient.role}` : '',
        isCompanyLevel: true,
      },
    });
    reset();
  }, [aiOutreachDomain, selectedRecipient, reset, tracking]);

  const handleClose = useCallback(() => {
    tracking.trackClose();
    if (isLoading) {
      stop();
    }
    appStore.setAiOutreachOpen(false);
  }, [isLoading, stop, tracking]);

  const handleSelectRecipient = useCallback(
    (recipient: Recipient | null) => {
      if (recipient) {
        tracking.trackSelectRecipient(recipient.name);
      } else {
        tracking.trackRemoveRecipient();
      }
      setSelectedRecipient(recipient);
      reset();
    },
    [reset, tracking]
  );

  const handleSendMessage = useCallback(
    (key: MessageKey, message: string) => {
      tracking.trackSendMessage(key, message);
      if (key === 'replace_competitors') {
        setShowCompetitorPicker(true);
        return;
      }
      if (key === 'alternative_insights' && emailPreviews.length > 0) {
        addUserMessage(message);
        setShowAlternatives(true);
        return;
      }
      sendMessage(message);
    },
    [addUserMessage, sendMessage, emailPreviews, tracking]
  );

  useEffect(() => {
    if (isFakeLoading) {
      const timer = setTimeout(() => {
        setIsFakeLoading(false);
      }, FAKE_LOADING_DELAY);
      return () => clearTimeout(timer);
    }
  }, [isFakeLoading]);

  const handleApplyCompetitors = useCallback(
    (newCompetitors: CraftEmailCompetitor[]) => {
      tracking.trackApplyCompetitors(newCompetitors.length);
      setShowCompetitorPicker(false);
      applyCompetitors(newCompetitors);
    },
    [applyCompetitors, tracking]
  );

  const handleDismissCompetitorPicker = useCallback(() => {
    tracking.trackDismissCompetitorPicker();
    setShowCompetitorPicker(false);
  }, [tracking]);

  const handleApplyPreview = useCallback(
    (preview: EmailPreview, index: number) => {
      tracking.trackApplyPreview(index);
      setShowAlternatives(false);
      setIsFakeLoading(true);
      setTimeout(() => {
        applyPreview(preview);
      }, FAKE_LOADING_DELAY);
    },
    [applyPreview, tracking]
  );

  const handleDismissAlternatives = useCallback(() => {
    tracking.trackDismissAlternatives();
    setShowAlternatives(false);
  }, [tracking]);

  return {
    domain: aiOutreachDomain,
    companyName: aiOutreachCompanyName,
    emailPreview: email,
    displayBodyBefore: email?.bodyBefore,
    displayBodyAfter: email?.bodyAfter,
    isInitialLoading,
    isFollowUpLoading: isFollowUpLoading || isFakeLoading,
    timeline,
    scrollToTopAfterUserSend,
    isError,
    onRetry: async () => {
      tracking.trackRetry();
      await refetchCreate();
    },
    selectedRecipient,
    threadId,
    messageId,
    emailPreviews,
    competitors,
    showAlternatives,
    showCompetitorPicker,
    onGenerate: handleGenerate,
    onClose: handleClose,
    onSelectRecipient: handleSelectRecipient,
    onSendMessage: handleSendMessage,
    onApplyPreview: handleApplyPreview,
    onDismissAlternatives: handleDismissAlternatives,
    onApplyCompetitors: handleApplyCompetitors,
    onDismissCompetitorPicker: handleDismissCompetitorPicker,
  };
};
